import React, { useState, useEffect } from "react"; 
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "./index.css"; 

import { Joyride } from 'react-joyride'; 
import { usePageTour } from './hooks/usePageTour'; 
import { Map, X } from 'lucide-react'; 

// Auth & Theme Context & Gatekeeper Imports
import { ThemeProvider, useTheme } from './context/ThemeContext'; // <-- ADDED USE THEME
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthModal from './components/AuthModal';

// Existing Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import HeroSection from "./components/HeroSection"; 
import ExploreStates from "./components/ExploreStates"; 
import TravelerTypes from "./components/TravellerTypes";
import ExperienceGrid from "./components/ExperienceGrid";
import TravelerStories from "./components/TravelerStories";
import SearchResults from "./components/SearchResults";
import StateDetails from "./components/StateDetails"; 

import HoverChatbot from "./components/HoverChatBot";
import WalkingCursor from "./components/WalkingCursor";
import GlobalBackButton from "./components/GlobalBackButton"; 

// Pages
import PlanTrip from "./pages/PlanTrip";
import StoriesPage from "./pages/StoriesPage";
import ItinerariesPage from "./pages/ItinerariesPage";
import LodgesPage from "./pages/LodgesPage";
import ExperiencesPage from "./pages/ExperiencesPage";
import MissionPage from "./pages/MissionPage";
import AboutPage from "./pages/AboutPage";
import RoutePlannerPage from "./pages/RoutePlannerPage";
import TripDiscovererPage from './pages/TripDiscovererPage';
import BlogPage from './pages/BlogPage';
import GamePage from "./pages/GamePage";
import CareersPage from "./pages/CareersPage";
import VehicleOptions from "./pages/VehicleOptions";
import WishlistPage from "./pages/WishlistPage";
import MeetTheTeam from "./pages/MeetTheTeam";
import ProfilePage from "./pages/ProfilePage";
import VlogsPage from "./pages/VlogsPage";
import TripsPage from "./pages/TripsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import MagazinesPage from "./pages/MagazinesPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null; 
};

// --- TUTORIAL & HOME PAGE WRAPPER ---
const HomePageWrapper = ({ onSearch, searchQuery, searchResults }) => {
  const { runTour, showPrompt, handleTourCallback, startTour, skipTour } = usePageTour('hasSeenHomeTour');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { isDark } = useTheme(); // 👈 Access Theme State

  const rawSteps = [
    { target: 'body', content: 'Welcome to Odessey! Let’s take a quick look at how to navigate the platform.', placement: 'center', disableBeacon: true },
    { target: '.tour-nav-explore', content: 'Discover hidden gems, browse unique experiences.', placement: 'bottom', disableBeacon: true },
    { target: '.tour-nav-plan', content: 'Ready to pack? Build a custom day-by-day itinerary or browse our ready-made travel plans.', placement: 'bottom', disableBeacon: true },
    { target: '.tour-nav-services', content: 'Book premium eco-lodges, map out complex road trips, and secure your travel vehicles all in one place.', placement: 'bottom', disableBeacon: true },
    { target: '.tour-nav-community', content: 'Connect with fellow travellers, read insightful blogs, and share your own adventures.', placement: 'bottom', disableBeacon: true },
    { target: '.tour-search-bar', content: 'Start your journey by searching for a destination right here.', placement: 'bottom', disableBeacon: true },
    { target: '.tour-home-states', content: 'Not sure where to go? Browse through our beautifully curated guides for every state in India.', disableBeacon: true, placement: 'top', styles:{tooltip: { transform: 'translateY(180px)', zIndex: 10000 }, tooltipArrow: { display: 'none' }}  },
    { target: '.tour-home-types', content: 'Select your personal travel style—from Backpacking to Luxury—to get perfectly tailored recommendations.', disableBeacon: true, placement: 'top', styles:{tooltip: { transform: 'translateY(180px)', zIndex: 10000 }, tooltipArrow: { display: 'none' }} },
    { target: '.tour-home-experiences', content: 'Find unique, hand-picked local activities that go beyond standard sightseeing.', disableBeacon: true, placement: 'top', styles:{tooltip: { transform: 'translateY(180px)', zIndex: 10000 }, tooltipArrow: { display: 'none' }} },
    { target: '#traveler-diaries', content: 'Get inspired by real travel stories from our community, or write and share your own adventures!', disableBeacon: true, placement: 'top', styles:{tooltip: { transform: 'translateY(180px)', zIndex: 10000 }, tooltipArrow: { display: 'none' }} },
    { target: '.tour-chatbot', content: 'Have questions or need instant help? Our AI assistant is here 24/7 to guide your journey!', disableBeacon: true, placement: 'left' }
  ];

  const tourSteps = rawSteps.map((step, index) => ({
    ...step,
    title: "Odessey Tour (" + (index + 1) + " of " + rawSteps.length + ")"
  }));

  return (
    <>
      {/* --- THE STARTUP PROMPT MODAL --- */}
      {showPrompt && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(17, 24, 39, 0.7)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box', animation: 'fadeIn 0.3s ease-out' }}>
          <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? '#f9fafb' : '#111827', borderRadius: '24px', padding: '30px 20px', maxWidth: '420px', width: '100%', boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.6)' : '0 25px 50px -12px rgba(0,0,0,0.5)', textAlign: 'center', position: 'relative', animation: 'slideUp 0.4s ease-out', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box', transition: 'background-color 0.3s, color 0.3s, border-color 0.3s' }}>
            <button onClick={() => skipTour(dontShowAgain)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#9ca3af' : '#9ca3af' }}>
              <X size={22} />
            </button>
            <div style={{ width: '56px', height: '56px', backgroundColor: isDark ? '#064e3b' : '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', transition: 'background-color 0.3s' }}>
              <Map size={28} color="#16a34a" />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'inherit', marginBottom: '8px' }}>Welcome to Odessey!</h2>
            <p style={{ fontSize: '14px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.6', marginBottom: '24px', transition: 'color 0.3s' }}>Would you like a quick guided tour to help you navigate our platform and discover all our travel features?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <button onClick={() => startTour(dontShowAgain)} style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '12px', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}>
                Start the tour
              </button>
              <button onClick={() => skipTour(dontShowAgain)} style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563', padding: '12px', borderRadius: '12px', fontSize: '15px', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#4b5563' : '#e5e7eb'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#f3f4f6'}>
                Skip for now
              </button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280' }}>
              <input type="checkbox" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#16a34a' }} />
              Do not show this message again
            </label>
          </div>
        </div>
      )}

      {/* --- THE JOYRIDE COMPONENT --- */}
      {runTour && (
        <Joyride
          steps={tourSteps} run={runTour} continuous={true} showSkipButton={true} showProgress={false} 
          callback={handleTourCallback} beaconComponent={() => null} scrollOffset={150} 
          styles={{
            options: { primaryColor: '#16a34a', zIndex: 10000, textColor: '#334151' },
            buttonClose: { color: '#9ca3af', width: '24px', height: '24px', marginTop: '5px' }, 
            tooltip: { borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', padding: '20px' },
            tooltipTitle: { fontSize: '15px', fontWeight: '800', color: '#16a34a', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px' },
            buttonNext: { borderRadius: '8px', fontWeight: 'bold' },
            buttonBack: { color: '#64748b' }, buttonSkip: { color: '#94a3b8' }
          }}
          locale={{ back: 'Back', close: 'Close', last: 'Finish Tour', next: 'Next', skip: 'Skip Tour' }}
        />
      )}

      <div className="tour-home-hero"><HeroSection onSearch={onSearch} /></div>
      <div className="tour-home-states"><ExploreStates /></div>
      <div className="tour-home-types"><TravelerTypes /></div>
      <div className="tour-home-experiences"><ExperienceGrid /></div>
      <div id="traveler-diaries"><TravelerStories /></div>
      <div id="search-results"><SearchResults searchQuery={searchQuery} searchResults={searchResults} /></div>

      <style dangerouslySetInnerHTML={{ __html: "@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } } @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }" }} />
    </>
  );
};

// --- MAIN APP COMPONENT ---
function App() {
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isWelcomeAuthOpen, setIsWelcomeAuthOpen] = useState(false);

  useEffect(() => {
    const hasSeenWelcome = sessionStorage.getItem('has_seen_welcome_auth');
    if (!hasSeenWelcome) {
      setIsWelcomeAuthOpen(true);
      sessionStorage.setItem('has_seen_welcome_auth', 'true');
    }
  }, []);

  const handleSearch = (place) => {
    setSearchQuery(place.name);
    setSearchResults([place]);
    setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <GlobalBackButton /> 
          
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100 transition-colors duration-300">
            <Header />
            
            <Routes>
              {/* ========================================================
                  PUBLIC ROUTES
              ======================================================== */}
              <Route path="/" element={<HomePageWrapper onSearch={handleSearch} searchQuery={searchQuery} searchResults={searchResults} />} />
              
              <Route path="/about" element={<AboutPage />} />
              <Route path="/mission" element={<MissionPage />} />
              <Route path="/team" element={<MeetTheTeam />} />
              <Route path="/careers" element={<CareersPage />} />

              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/story" element={<StoriesPage />} />
              <Route path="/stories" element={<StoriesPage />} />
              <Route path="/vlogs" element={<VlogsPage />} />
              <Route path="/discover" element={<TripDiscovererPage />} />
              <Route path="/group-trips" element={<TripsPage />} />
              
              <Route path="/place/:placeName" element={<StateDetails />} />
              <Route path="/state/:stateName" element={<StateDetails />} />

              {/* ========================================================
                  PROTECTED ROUTES
              ======================================================== */}
              <Route path="/plan" element={<ProtectedRoute><PlanTrip /></ProtectedRoute>} />
              <Route path="/route-planner" element={<ProtectedRoute><RoutePlannerPage /></ProtectedRoute>} />
              <Route path="/itinerary" element={<ProtectedRoute><ItinerariesPage /></ProtectedRoute>} />
              <Route path="/experiences" element={<ProtectedRoute><ExperiencesPage /></ProtectedRoute>} />
              <Route path="/vehicles" element={<ProtectedRoute><VehicleOptions /></ProtectedRoute>} />
              <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/games" element={<ProtectedRoute><GamePage /></ProtectedRoute>} />
              <Route path="/library" element={<ProtectedRoute><MagazinesPage /></ProtectedRoute>} />
            </Routes>

            <HoverChatbot />
            <WalkingCursor />
            <Footer />
          </div>

          <AuthModal 
            isOpen={isWelcomeAuthOpen} 
            onClose={() => setIsWelcomeAuthOpen(false)} 
          />

        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;